import { useCallback, useState } from 'react';
import { sendNotification } from 'utils/helperMethods';
import type { SimulatorAppliedFilters, SimulatorFilterCategory } from './SimulatorFilterPanel';
import type { SimulatorFilterScopeItem, SimulatorTableSearchInput } from './simulatorFilter.helpers';
import { resolveSimulatorTableFiltersViaApplyFilter } from './simulatorFilterApply.helpers';

type ApplySimulatorFiltersArgs = {
    projectId?: number | string;
    scenarioId?: number | string;
    scenarioTabId?: number | string | null;
    filterCategories: SimulatorFilterCategory[];
    appliedScope?: SimulatorFilterScopeItem[];
    appliedFilters: SimulatorAppliedFilters;
    searchInput?: SimulatorTableSearchInput;
};

export function useApplySimulatorFilters() {
    const [isApplying, setIsApplying] = useState(false);

    const applySimulatorFilters = useCallback(
        async ({
            scenarioTabId,
            filterCategories,
            appliedScope,
            appliedFilters,
        }: ApplySimulatorFiltersArgs): Promise<Record<string, number[]> | null> => {
            if (scenarioTabId == null) {
                sendNotification('error', 'Active lens is unavailable. Select a lens tab and try again.');
                return null;
            }

            setIsApplying(true);
            try {
                return await resolveSimulatorTableFiltersViaApplyFilter({
                    filterCategories,
                    categorySelections: appliedFilters.categorySelections,
                    appliedScope,
                });
            } catch (error: unknown) {
                const detail =
                    (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail?.trim() ||
                    'Failed to apply filters. Please try again.';
                sendNotification('error', detail);
                return null;
            } finally {
                setIsApplying(false);
            }
        },
        [],
    );

    return {
        applySimulatorFilters,
        isApplying,
    };
}
